// Web implementation of the SyQi video-mode avatar. Renders the avatar GLB
// directly inside the app with plain three.js in a div-managed canvas — no
// iframe, no hosted stage page, no react-three-fiber wrapper (r3f's DOM
// Canvas doesn't size/paint reliably under Metro's RN-web build; a raw
// WebGLRenderer + our own RAF loop does). All rig/pose/camera/lip-sync math
// lives in the platform-agnostic ./avatarScene so web and native stay in
// lockstep. Audio playback lives in SyqiOverviewVideo — this component only
// animates morphs against the lipsync clock it's handed via play()/tick().
import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import * as THREE from 'three';
import {
  addLights,
  AvatarAnimator,
  buildRig,
  frameCamera,
  loadAvatarScene,
} from './avatarScene';
import type { VideoAvatarHandle, VideoAvatarProps, LipsyncFrame } from './videoAvatarTypes';

type QueuedCmd =
  | { type: 'play'; id: string; lipsync: LipsyncFrame[]; startAt?: number }
  | { type: 'tick'; t: number }
  | { type: 'stop' };

export const VideoAvatar = forwardRef<VideoAvatarHandle, VideoAvatarProps>(function VideoAvatar(
  { onReady, onEnded, onError, style, glbUrl },
  ref
) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const animatorRef = useRef<AvatarAnimator | null>(null);
  const queueRef = useRef<QueuedCmd[]>([]);

  const applyCmd = (animator: AvatarAnimator, cmd: QueuedCmd) => {
    if (cmd.type === 'play') animator.play(cmd.id, cmd.lipsync, cmd.startAt ?? 0);
    else if (cmd.type === 'tick') animator.tick(cmd.t);
    else animator.stop();
  };

  const flushOrQueue = (cmd: QueuedCmd) => {
    const animator = animatorRef.current;
    if (!animator) {
      queueRef.current.push(cmd);
      return;
    }
    applyCmd(animator, cmd);
  };

  useImperativeHandle(
    ref,
    () => ({
      play: (id: string, lipsync: LipsyncFrame[], startAt?: number) => flushOrQueue({ type: 'play', id, lipsync, startAt }),
      tick: (t: number) => flushOrQueue({ type: 'tick', t }),
      stop: () => flushOrQueue({ type: 'stop' }),
    }),
    []
  );

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    let disposed = false;
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.domElement.style.width = '100%';
    renderer.domElement.style.height = '100%';
    renderer.domElement.style.display = 'block';
    host.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(28, 1, 0.05, 50);
    addLights(scene);

    const resize = () => {
      const w = Math.max(1, host.clientWidth);
      const h = Math.max(1, host.clientHeight);
      renderer.setSize(w, h, false);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    };
    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(host);

    loadAvatarScene(glbUrl)
      .then((root) => {
        if (disposed) return;
        scene.add(root);
        const rig = buildRig(root);
        frameCamera(camera, root);
        resize();
        animatorRef.current = new AvatarAnimator(rig, onEnded);
        const queued = queueRef.current;
        queueRef.current = [];
        for (const cmd of queued) applyCmd(animatorRef.current, cmd);
        onReady?.();
      })
      .catch((err) => {
        if (!disposed) onError?.(err instanceof Error ? err.message : 'failed to load avatar');
      });

    const start = performance.now();
    let raf = 0;
    const loop = (now: number) => {
      raf = requestAnimationFrame(loop);
      animatorRef.current?.update((now - start) / 1000);
      renderer.render(scene, camera);
    };
    raf = requestAnimationFrame(loop);

    return () => {
      disposed = true;
      cancelAnimationFrame(raf);
      observer.disconnect();
      renderer.dispose();
      if (renderer.domElement.parentNode === host) host.removeChild(renderer.domElement);
      animatorRef.current = null;
    };
    // onReady/onEnded/onError are stable per-mount callbacks from the caller;
    // only glbUrl should re-trigger a full scene rebuild (a deity's GLB can
    // change if resolveGlbUrl() flips from the SyQi fallback to a real model).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [glbUrl]);

  return <div ref={hostRef} style={{ width: '100%', height: '100%', overflow: 'hidden', ...(style as object) }} />;
});

export default VideoAvatar;
